<template>
  <SearchableModel
    v-bind="$attrs"
    model-name="Organization"
    :available-filters="availableFilters"
    :sort-options="sortOptions"
    sort="name"
    push-state
  >
    <template #default="{ models }">
      <div class="card">
        <ul class="list-group list-group-flush">
          <li
            v-for="organization in models"
            :key="organization.id"
            class="list-group-item list-group-item-action d-flex align-items-center"
          >
            <div class="mr-3">
              <Avatar :username="organization.name" size="50" />
            </div>
            <div class="flex-grow-1">
              <a :href="organization.links.show">
                <strong>{{ organization.name }}</strong>
              </a>
              <small v-if="organization.parent_name" class="text-muted tw:flex tw:items-center tw:gap-1">
                <NetworkIcon :size="12" /> {{ organization.parent_name }}
              </small>
            </div>
            <div class="ml-3 d-none d-sm-block text-center" v-if="!isNaN(organization.tours_count)">
              <p class="mb-0">
                <small class="text-muted tw:uppercase">{{ $t('Events') }}</small><br>
                <strong v-if="organization.max_tours_number === null"> ∞ </strong>
                <strong v-else :class="{
                  'text-danger': organization.tours_count >= organization.max_tours_number,
                  'text-warning': organization.tours_count / organization.max_tours_number > 0.3 }">
                  {{ organization.tours_count }}/{{ organization.max_tours_number }}
                </strong>
              </p>
            </div>
            <div class="ml-3 d-none d-sm-block text-center" v-if="!isNaN(organization.exponents_count)">
              <p class="mb-0">
                <small class="text-muted tw:uppercase">{{ $t('Exponents') }}</small><br>
                <strong v-if="organization.max_exponents_number === null">∞</strong>
                <strong v-else :class="{
                  'text-danger': organization.max_exponents_number <= organization.exponents_count,
                  'text-warning': organization.exponents_count / organization.max_exponents_number > 0.3 }">
                  {{ organization.exponents_count }}/{{ organization.max_exponents_number }}
                </strong>
              </p>
            </div>
            <div class="ml-3 d-none d-sm-block text-center">
              <p class="mb-0">
                <small class="text-muted tw:uppercase">{{ $t('Subscription') }}</small><br>
                <strong v-if="organization.subscription_end" :class="{
                  'text-danger': isExpired(organization.subscription_end),
                  'text-warning': isExpiring(organization.subscription_end) }">
                  {{ new Date(organization.subscription_end).toLocaleDateString() }}
                </strong>
                <strong v-else>∞</strong>
              </p>
            </div>
            <template v-if="organization.terms_acceptances">
              <div class="ml-3 d-none d-sm-block text-center">
                <p class="mb-0">
                  <small class="text-muted tw:uppercase">{{ $t('Terms of use') }}</small><br>
                  <strong>
                    <template v-if="organization.terms_acceptances[0]?.accepted_at">
                      {{ new Date(organization.terms_acceptances[0].accepted_at).toLocaleDateString() }}
                    </template>
                    <template v-else>
                      <span class="text-danger">{{ $t('Not accepted') }}</span>
                    </template>
                  </strong>
                </p>
              </div>
            </template>
          </li>
        </ul>
      </div>
    </template>
  </SearchableModel>
</template>

<script setup>
import SearchableModel from '@/components/searchable-model/SearchableModel.vue'
import Avatar from '@/components/ui/Avatar.vue'
import { NetworkIcon } from '@lucide/vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  availableFilters: {
    type: Array,
    default: () => [],
  },
  /**
   * How many days before the end a subscription starts being flagged. Comes from
   * Organization::SUBSCRIPTION_WARNING_DAYS so the list, the organization page and the
   * reminders all use the one threshold.
   */
  warningDays: {
    type: Number,
    default: 60,
  },
})

const sortOptions = [
  { value: 'name', label: 'Name' },
  { value: 'subscription_end', label: 'Subscription end date' },
  { value: 'tours_count', label: 'Number of events' },
  { value: 'exponents_count', label: 'Number of exhibitors' },
]

function isExpired(date) {
  return new Date(date) < new Date()
}

function isExpiring(date) {
  const limit = new Date()
  limit.setDate(limit.getDate() + props.warningDays)

  return !isExpired(date) && new Date(date) <= limit
}
</script>
